import ChartCard from "@ui/Highcharts/ui/Card";
import React from "react";
import { css, cx } from "@linaria/core";
import { spacings } from "@ui/Theme";
import { FullViewportButtonMarker } from "@ui/FullViewportButton";
import { exportWidgetTarget } from "@ui/ExportWidgetButton";
import HighchartsComponent from "@ui/Highcharts";
import { useSettings } from "../../../../../../../../api/useSettings";
import { useCanUserDoOnProject } from "../../../../../../../canUserDo";
import { useGroupUnitState } from "../../../../../../Measures/GroupUnitInput";
import { useDisplayInvalidPoints } from "../../../../../../Analysis/ToggleDisplayInvalidPoints";
import { usePublicationFlagDisplay } from "../../../../../../Analysis/TogglePublicationDisplay";
import { useEventPeriod } from "../../../../../hooks/useEventPeriod";
import useOptions from "./hooks/useOptions";
import CommonLegend from "../../../../../../Chart/Legend/CommonLegend";
import MeasuresLegend from "../../../../../../Chart/Legend/MeasuresLegend";
import { useQuantityNoDataIntervals } from "../../../../../../useNoDataIntervals";
import useSeries from "../../../../../../Measures/useSeries";
import type { Event } from "../../../../../../../../api/useEvents";
import type { Project } from "../../../../../../../../api/useProjects";
import type { Analysis } from "../../../../../../../../api/Analysis";
import type { NonEmptyArray } from "diagnostics-lib/array";
import type { ActiveAnalysis } from "../../../../../../../../api/useActiveAnalysesStream";
import type { Source } from "../../../../../../../../api/useSources";
import type { SelectedQuantity } from "../../../../../../useSelectedQuantities";
import type { MeasuresChartType } from "../../../../../../Measures/MeasuresChartType";

interface Props {
  title: React.JSX.Element;
  subtitle: React.JSX.Element;
  legendLeft?: React.ReactNode;
  project: Project;
  analyses: Analysis[];
  activeAnalyses: ActiveAnalysis[];
  source: Source;
  selectedQuantities: NonEmptyArray<SelectedQuantity>;
  selectedChartOption: MeasuresChartType;
  allAnalysesMode: boolean;
  event: Event;
}

const MeasureHighCharts = ({
  title,
  subtitle,
  legendLeft,
  project,
  analyses,
  activeAnalyses,
  source,
  selectedQuantities,
  selectedChartOption,
  allAnalysesMode,
  event,
}: Props) => {
  const settings = useSettings();
  const canUpdateQuantityValues = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const canUserWriteProject = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const actionAllowed = canUserWriteProject;

  const [axisGroupedByUnit] = useGroupUnitState({
    scope: project.id,
  });
  const [displayInvalidPoints] = useDisplayInvalidPoints({
    scope: project.id,
  });
  const [publicationFlagDisplay] = usePublicationFlagDisplay({
    scope: project.id,
    defaultValue: settings.DISPLAY_PUBLICATION_MARKERS,
  });

  const [period, _, __, ___, ____, defaultFaultEventPeriod] = useEventPeriod(
    allAnalysesMode
      ? {
          event,
          source,
          project,
          boundaries: "analyses",
        }
      : {
          event,
          source,
          project,
          analyses: activeAnalyses,
          boundaries: "active-analyses",
        },
  );

  const chartRef = React.useRef<Highcharts.Chart>(null);

  const series = useSeries(
    project,
    analyses,
    activeAnalyses,
    selectedQuantities,
    selectedQuantities.length > 1 ? axisGroupedByUnit : false,
    actionAllowed,
    selectedChartOption,
    allAnalysesMode,
    publicationFlagDisplay,
  );

  const options = useOptions({
    chartRef,
    selectedQuantities,
    series,
    analyses,
    activeAnalyses,
    project,
    source,
    axisGroupedByUnit: selectedQuantities.length > 1 ? axisGroupedByUnit : false,
    multiColor: true,
    timeZone: project.timezone,
    canUpdateQuantityValues,
    period,
    selectedChartOption,
    allAnalysesMode,
    event,
  });

  const noDataIntervals = useQuantityNoDataIntervals(
    activeAnalyses,
    selectedQuantities,
    selectedChartOption,
    project.id,
  );

  const legend = React.useMemo(
    () => (
      <>
        <MeasuresLegend
          chartRef={chartRef}
          selectedChartOption={selectedChartOption}
          selectedQuantities={selectedQuantities}
          series={series}
          period={period}
          displayInvalidPoints={displayInvalidPoints}
        />
        <CommonLegend
          chartRef={chartRef}
          analyses={analyses}
          series={series}
          period={period}
          noDataIntervals={noDataIntervals}
          allAnalysesMode={allAnalysesMode}
          defaultFaultEventPeriod={defaultFaultEventPeriod}
          chartType="measures"
          projectId={project.id}
        />
      </>
    ),
    [
      selectedChartOption,
      selectedQuantities,
      series,
      period,
      displayInvalidPoints,
      analyses,
      noDataIntervals,
      allAnalysesMode,
      defaultFaultEventPeriod,
      project.id,
    ],
  );

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      legendLeft={legendLeft}
      legend={legend}
      className={cx(measureChartCard, FullViewportButtonMarker, exportWidgetTarget)}
    >
      <HighchartsComponent
        name="Measures"
        ref={chartRef}
        options={options}
        selectablePoints
        normalizeNavigatorSeries
        timezone={project.timezone}
        range={!allAnalysesMode}
      />
    </ChartCard>
  );
};

export default MeasureHighCharts;

//#region CSS
const measureChartCard = css`
  min-height: 600px;
  row-gap: ${spacings.m};
`;
//#endregion CSS
